import { hasRenderableRating, hasRenderableThought } from '@/utils/activityContent';
import { resolveUserProfileTypeWithFallback } from '@/utils/userProfileImage';

/**
 * @typedef {'post' | 'article' | 'thought' | 'rating' | 'rating-prompt' | 'artlist' | 'artwork'} CommunityFeedItemKind
 */

/**
 * The UI-safe actor shape consumed by all Community cards.
 * @typedef {Object} CommunityActor
 * @property {string|null} image
 * @property {string|null} username
 * @property {string} profileType
 */

/**
 * Normalized feed item. Each `kind` has a dedicated renderer in CommunityFeedItem.
 * API-specific fields must be resolved here rather than in the cards.
 * @typedef {Object} CommunityFeedItem
 * @property {CommunityFeedItemKind} kind
 * @property {Object} [data]
 * @property {Object} [article]
 * @property {Object} [artlist]
 * @property {Object} [artwork]
 * @property {string|Date|null} [createdAt]
 * @property {CommunityActor} [actor]
 */

function asObject(value) {
  return value && typeof value === 'object' ? value : {};
}

/**
 * @param {Object|string|null|undefined} value API actor payload.
 * @param {Partial<CommunityActor>} overrides Explicit activity-level actor fields.
 * @returns {CommunityActor}
 */
export function createCommunityActor(value, overrides = {}) {
  const actor = asObject(value);

  return {
    image: overrides.image ?? actor.user_image?.image_id ?? null,
    username: overrides.username ?? actor.user_username ?? null,
    profileType: resolveUserProfileTypeWithFallback(
      'collector',
      overrides.profileType,
      actor,
    ),
  };
}

/** @returns {CommunityFeedItem|null} */
function normalizeRatingItem(data, viewerId) {
  if (!hasRenderableRating(data)) {
    return null;
  }

  const ratingCreator = data?.rating_creator;
  const ratingCreatorId = typeof ratingCreator === 'string'
    ? ratingCreator
    : ratingCreator?._id;
  const isCreator = ratingCreatorId === viewerId;
  const myRating = data.my_rating || data.myRating || (isCreator ? data : null);
  const hasRated = Boolean(myRating);

  return {
    kind: hasRated ? 'rating' : 'rating-prompt',
    data: {
      ...data,
      rating_artwork: {
        ...data.rating_artwork,
        slug: data?.rating_artwork?.slug || data?.rating_artwork?._id,
      },
      ...(hasRated && myRating?.rating_values
        ? { my_rating: { rating_values: myRating.rating_values } }
        : {}),
    },
    actor: createCommunityActor(ratingCreator),
  };
}

/** @returns {CommunityFeedItem} */
function normalizeArticleItem(article) {
  const creator = article.creator || article.article_creator || {};

  return {
    kind: 'article',
    article,
    actor: createCommunityActor(creator, {
      image: article.creator_image || creator.user_image?.image_id || creator.image_id || null,
      username: article.creator_username || creator.user_username || creator.username || null,
      profileType: article.creator_type,
    }),
  };
}

/** @returns {CommunityFeedItem|null} */
function normalizeArtlistItem(data) {
  const artlist = data?.artlist || data;
  const artworkCount = artlist?.artlist_artworks?.length || 0;
  if (![10, 20, 30].includes(artworkCount)) {
    return null;
  }

  const creator = data?.user || artlist?.artlist_creator || {};
  return {
    kind: 'artlist',
    artlist,
    createdAt: data?.createdAt || artlist?.createdAt,
    actor: createCommunityActor(creator),
  };
}

/** @returns {CommunityFeedItem} */
function normalizeArtworkItem(data, item) {
  const uploader = data?.artwork_about?.artwork_gallery || item?.artwork_user;

  return {
    kind: 'artwork',
    artwork: data,
    actor: createCommunityActor(uploader),
  };
}

/**
 * Converts a raw Community API entry into the presentation contract.
 * Add new event types here, then add their renderer and a fixture-based test.
 *
 * @param {Object|null|undefined} item Raw Community API entry.
 * @param {string|null} viewerId Current viewer identifier.
 * @returns {CommunityFeedItem|null}
 */
export function normalizeCommunityFeedItem(item, viewerId = null) {
  const data = item?.data ?? {};

  switch (item?.type) {
  case 'post':
    return { kind: 'post', data };

  case 'article':
    return normalizeArticleItem(data);

  case 'thought':
    return hasRenderableThought(data)
      ? { kind: 'thought', data, actor: createCommunityActor(data.thought_creator) }
      : null;

  case 'rating':
    return normalizeRatingItem(data, viewerId);

  case 'artlist':
    return normalizeArtlistItem(data);

  case 'artwork':
    return normalizeArtworkItem(data, item);

  default:
    console.warn('[COMMUNITY] tipo no soportado:', item);
    return null;
  }
}
